/**
 * Header: Alpine `headerRoot` (search/cart, drawer scroll lock + scrollbar gap) and `<header-section>`.
 * Load before alpine.js in theme.liquid so `alpine:init` runs after the listener below is registered.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('headerRoot', () => ({
    cartOpen: false,
    searchOpen: false,
    searchTerm: '',

    init() {
      this.searchTerm = this.$el.dataset.initialSearchTerms || '';

      const mq = window.matchMedia('(min-width: 769px)');
      let searchDrawerSb = null;

      const syncSearchDrawerBody = () => {
        const on = mq.matches && this.searchOpen;

        if (!on) {
          document.body.classList.remove('search-drawer-open');
          document.documentElement.style.removeProperty('--search-drawer-scrollbar-gap');
          searchDrawerSb = null;
          return;
        }

        if (searchDrawerSb === null) {
          searchDrawerSb = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        }

        document.body.classList.add('search-drawer-open');

        if (searchDrawerSb) {
          document.documentElement.style.setProperty('--search-drawer-scrollbar-gap', `${searchDrawerSb}px`);
        } else {
          document.documentElement.style.removeProperty('--search-drawer-scrollbar-gap');
        }
      };

      this.$watch('searchOpen', syncSearchDrawerBody);
      mq.addEventListener('change', syncSearchDrawerBody);
      syncSearchDrawerBody();
    },
  }));
});

function syncCartBubbleAriaLabel(bubble) {
  const template = bubble.getAttribute('data-cart-aria-template');
  if (!template) return;
  const countEl = bubble.querySelector('[data-cart-count]');
  const raw = countEl ? countEl.textContent : '0';
  const count = parseInt(raw, 10);
  const n = Number.isFinite(count) ? count : 0;
  bubble.setAttribute('aria-label', template.replace(/__COUNT__/g, String(n)));
}

export class HeaderSection extends HTMLElement {
  connectedCallback() {
    this.sectionRoot = this.closest('[id^="shopify-section"]');
    if (!this.sectionRoot) return;

    this.lastScrollTop = 0;
    this.onResize = () => this.updateHeaderHeight();
    this.onScroll = () => this.handleScroll();
    this.onCartRequestEnd = (event) => this.handleCartRequestEnd(event);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);

    this.updateHeaderHeight();
    this.handleScroll();

    const bubble = document.getElementById('header-cart-bubble');
    if (bubble) {
      syncCartBubbleAriaLabel(bubble);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  updateHeaderHeight() {
    if (!this.sectionRoot) return;
    const headerHeight = this.sectionRoot.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  handleScroll() {
    if (!this.sectionRoot) return;
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScroll > 0) {
      this.sectionRoot.classList.add('scrolled-header');
    } else {
      this.sectionRoot.classList.remove('scrolled-header');
    }

    if (currentScroll > this.lastScrollTop) {
      this.sectionRoot.classList.add('header-sticky');
    } else if (currentScroll < this.lastScrollTop) {
      this.sectionRoot.classList.remove('header-sticky');
    }

    if (currentScroll === 0) {
      this.sectionRoot.classList.remove('header-sticky');
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }

  handleCartRequestEnd(event) {
    const cart = event.detail?.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    const bubble = document.getElementById('header-cart-bubble');
    if (!bubble) return;
    const template = bubble.getAttribute('data-cart-aria-template');
    if (!template) return;
    bubble.setAttribute('aria-label', template.replace(/__COUNT__/g, String(cart.item_count)));
  }
}

if (!customElements.get('header-section')) {
  customElements.define('header-section', HeaderSection);
}

function syncViewportHeight() {
  const h = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty('--viewport-height', `${h}px`);
}

syncViewportHeight();
window.addEventListener('resize', syncViewportHeight);
window.visualViewport?.addEventListener('resize', syncViewportHeight);
window.visualViewport?.addEventListener('scroll', syncViewportHeight);
